import React, { useState, useEffect, useRef, useContext } from "react";

import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import ProductVersionCommentsAPI from "../../API/productVersionsComments.api";
import CommentCard from "../../components/Cards/CommentCard";
import MainHeader from "../../components/Headers/MainHeader";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import UserContext from "../../lib/UserContext";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { longDate } from "../../utils/DateHelper";
import ProductsAPI from "../../API/products.api";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";

const MESSAGES_PER_HIT = 150;

const Comments = () => {
    const location = useLocation();
    const { t } = useTranslation();

    //Link State
    const publishedModel = location.state?.publishedModel || {};
    const productVersionId = location.state?.productVersionId || "";
    const previousPage = location.state?.previousPage || {
        label: t("publishedProducts"),
        path: "/published-products",
    };

    //Infinite Scrolling States
    const [, setIsLoading] = useState(false); //isLoading

    //Comment States
    const [loggedInUser, setLoggedInUser] = useState({});
    const [, setCommentsTotalCount] = useState(0); //commentsTotalCount
    const [, setStaticCommentsCount] = useState(0); //staticCommentsCount
    const [currentComment, setCurrentComment] = useState("");
    const [comments, setComments] = useState([]);
    const [productDetails, setProductDetails] = useState({});
    const commentsRef = useRef(0);

    const messagesEndRef = useRef(null);
    const context = useContext(UserContext);
    const history = useHistory();

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            hide: isEmpty(previousPage),
            path: previousPage.path,
            element: () => previousPage.label,
        },
        {
            element: () => (
                <Trans i18nKey="productComments">Product Comments</Trans>
            ),
        },
    ];

    useEffect(() => {
        if (!publishedModel) history.push(previousPage.path);
        setLoggedInUser(context.getUser());
        getComments();
        scrollToBottom();
        setProductDetails(publishedModel);
        if (!publishedModel.materialNames) getMaterialNames();
        // infiniteScrollingObserver();
    }, []);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({});
        }
    };

    // const infiniteScrollingObserver = () => {
    //     let intObserver = new IntersectionObserver(
    //         (entries) => {
    //             entries.forEach((entry) => {
    //                 // returns true if the target intersects the root element
    //                 if (
    //                     entry.isIntersecting &&
    //                     !isLoading &&
    //                     commentsRef.current + staticCommentsCount <
    //                         commentsTotalCount
    //                 ) {
    //                     getComments(
    //                         Math.floor(commentsRef.current / MESSAGES_PER_HIT)
    //                     );
    //                     console.log("Fetch Triggered");
    //                 }
    //             });
    //         },
    //         {
    //             // default options
    //         }
    //     );
    //     let target = document.querySelector("#targetId");
    //     intObserver.observe(target); // start observation
    // };

    const commentHandler = (e) => {
        setCurrentComment(e.target.value);
    };

    const renderComments = () => {
        return (
            comments &&
            comments.map((comment, index) => {
                return (
                    <CommentCard
                        commentData={comment}
                        alignRight={
                            comment?.createdBy?.role === loggedInUser.role
                        }
                        key={index}
                    />
                );
            })
        );
    };

    const getMaterialNames = async () => {
        if (publishedModel.productId) {
            const result = await ProductsAPI.getProductsInfo(
                publishedModel.productId
            );
            setProductDetails((prevDetails) => ({
                ...prevDetails,
                materialNames: result.materialNames,
            }));
        }
    };

    const submitComment = async () => {
        const trimmedComment = currentComment.trim();
        if (trimmedComment) {
            const result = await ProductVersionCommentsAPI.postComment({
                productVersionId,
                comment: trimmedComment,
            });
            if (result?.inserted) {
                setComments((prevCommemts) => {
                    return [
                        ...prevCommemts,
                        {
                            id: result.id,
                            comment: trimmedComment,
                            createdBy: {
                                ...loggedInUser,
                            },
                            createdAt: new Date(),
                        },
                    ];
                });
                setStaticCommentsCount((prevCount) => prevCount + 1);
                setCurrentComment("");
                scrollToBottom();
                LogCapture.info(
                    `comment is made on model with id ${publishedModel.id}`,
                    ENTITIES.publishedProducts,
                    ACTIONS.publishedProducts.comment
                );
            }
        }
    };

    const getComments = async (offset = null) => {
        setIsLoading(true);
        const result = await ProductVersionCommentsAPI.listComments(
            productVersionId,
            MESSAGES_PER_HIT,
            offset,
            "createdAt",
            "ASC"
        );
        setCommentsTotalCount(result?.totalCount);
        if (result?.data) {
            setComments((prevComments) => {
                if (!isEmpty(prevComments)) {
                    commentsRef.current = [
                        ...result.data,
                        ...prevComments,
                    ].length;
                    return [...result.data, ...prevComments];
                } else {
                    commentsRef.current = result.data.length;
                    return result.data;
                }
            });
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="steps step1 show">
                <BreadCrumb crumbs={breadCrumbs} />
                <MainHeader header={"Comments"} />
                <div className="card-table shadow flex justify-center w-full">
                    <div className="flex flex-col justify-end w-full h-128">
                        <div className="w-full h-20 border-b-2 border-secondary-color-more-lighter-blue">
                            <div className="market-header flex mb-2">
                                <img
                                    src={
                                        productDetails.image ||
                                        require("../../assets/images/neural.png")
                                            .default
                                    }
                                    className="user-img-noshrink"
                                />
                                <div className="ml-2">
                                    <h3 className="medium-title-bold">
                                        {productDetails.productName}
                                    </h3>
                                    <span className="market-item-type success">
                                        <span className="w-44 tooltip-container">
                                            {productDetails.materialNames?.join(
                                                ", "
                                            )}
                                        </span>
                                    </span>
                                </div>
                                <div className="ml-auto">
                                    <div className="flex">
                                        <span className="uppercase text-xxs text-secondary-color-lighter-blue my-auto">
                                            <Trans i18nKey="version">
                                                Version
                                            </Trans>
                                        </span>
                                        <p className="ml-1 text-xs text-primary-orange my-auto">
                                            {productDetails.productVersion}
                                        </p>
                                    </div>
                                    <div>
                                        {longDate(productDetails.publishedAt)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="hidden-scrollbar w-full h-full overflow-auto px-6 py-3">
                            <div id="targetId"></div>
                            {renderComments()}
                            <div ref={messagesEndRef}></div>
                        </div>
                        <div className="border-t-2 flex flex-col items-end border-secondary-color-more-lighter-blue">
                            <textarea
                                placeholder={"Add comment here..."}
                                className="input-element textarea-element mt-2"
                                value={currentComment || ""}
                                onChange={commentHandler}
                            ></textarea>
                            <button
                                onClick={submitComment}
                                className="secondary-green-btn mng-model modal-btn-open mt-2"
                            >
                                <Trans i18nKey="send">Send</Trans>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Comments;
